import Button from "@lhd/components/Button";
import {
  APP_PART_TYPES,
  UNIT_OF_MEASURE_TYPE,
} from "@lhd/features/Apps/AppForm/constants";
import { AppPartType } from "@lhd/features/Apps/AppForm/types";
import { Checkbox, Form, Input, Radio, Space } from "antd";
import { FormInstance } from "antd/es/form/Form";
import { FC, useContext, useRef, useState } from "react";
import { Styles, StylesInputFormula, StylesRadioItem } from "./styled";
import { FormActionContext } from "@lhd/features/Apps/AppForm";
import { ERRORS_MESSAGE } from "@lhd/constants";

type PropsType = {
  form: FormInstance<AppPartType>;
  getFieldClassName?: (name: string) => string;
};

const functionOptions = [
  {
    label: "IF(condition, value_if_true, value_if_false)",
    value: "IF()",
    description:
      "Returns one value if the specified condition is TRUE, and another value if it is FALSE.",
  },
  {
    label: "AND(condition1, condition2, ...)",
    value: "AND()",
    description:
      "Returns TRUE if all of the specified conditions are TRUE; returns FALSE if any of the specified conditions are FALSE.",
  },
  {
    label: "OR(condition1, condition2, ...)",
    value: "OR()",
    description:
      "Returns TRUE if any of the specified conditions are TRUE; returns FALSE if all of the specified conditions are FALSE.",
  },
  {
    label: "NOT(condition)",
    value: "NOT()",
    description:
      "Returns FALSE if the specified condition is TRUE; returns TRUE if the specified condition is FALSE.",
  },
  {
    label: 'CONTAINS(field_code, "search_string")',
    value: "CONTAINS()",
    description:
      "Tests whether a given string value is contained in a field. If the value is found, the function returns TRUE; if not, the function returns FALSE.",
  },
  {
    label: "ROUND(number, [places])",
    value: "ROUND()",
    description:
      "Rounds a number to the nearest whole number or to the specified number of decimal places.",
  },
  {
    label: "ROUNDUP(number, [places])",
    value: "ROUNDUP()",
    description:
      "Rounds a number up to the nearest whole number or to the specified number of decimal places.",
  },
  {
    label: "ROUNDDOWN(number, [places])",
    value: "ROUNDDOWN()",
    description:
      "Rounds a number down to the nearest whole number or to the specified number of decimal places.",
  },
  {
    label: "SUM(number1, [number2], ...)",
    value: "SUM()",
    description: "Adds values, including values of fields placed in tables.",
  },
  {
    label: 'DATE_FORMAT(field_code, "datetime_format", "timezone")',
    value: "DATE_FORMAT()",
    description:
      "Converts a value (e.g., a date, time, or datetime) to a string in the specified datetime format.",
  },
  {
    label: "YEN(number, places)",
    value: "YEN()",
    description:
      "Adds thousands separators and a ¥ (yen) symbol to a number and rounds it to the specified number of decimal places.",
  },
];

// const fieldCodeOptions = [
//   {
//     label: "number_1",
//     value: "number_1",
//     description: "Number",
//   },
//   {
//     label: "text_1",
//     value: "text_1",
//     description: "Text",
//   },
//   {
//     label: "updated_time",
//     value: "updated_time",
//     description: "Updated time",
//   },
// ];

const formulaDisplayType = [
  { label: "数 (1000)", value: "Number (1000)" },
  {
    label: "数（千の区切り付き）(1,000)",
    value: "Number with thousands separator (1,000)",
  },
  {
    label: "日付と時刻 (2012年8月6日 2:03 PM)",
    value: "Date & time (Aug 06, 2012 2:03 PM)",
  },
  {
    label: "日付 (2012年8月6日)",
    value: "Date (Aug 06, 2012)",
  },
  {
    label: "時刻 (2:03 PM)",
    value: "Time (2:03 PM)",
  },
  {
    label: "時間と分 (29時間47分)",
    value: "Hours & minutes (29 hours 47 minutes)",
  },
  {
    label: "日、時間、分 (1日5時間47分)",
    value: "Days & hours & minutes (1 day 5 hours 47 minutes)",
  },
];

const CalculatedSetting: FC<PropsType> = ({ form, getFieldClassName }) => {
  const [displayDropdown, setDisplayDropdown] = useState<{
    isOpen: boolean;
    type: "function" | "field_code";
  }>({
    isOpen: false,
    type: "function",
  });
  const { contextState } = useContext(FormActionContext);

  const fieldCodeOptions = contextState?.layout
    ?.filter((apItem) => apItem?.app_part_type !== APP_PART_TYPES.CALCULATED)
    ?.map((apItem) => ({
      label: apItem?.field_code,
      value: apItem?.field_code,
      description: apItem?.name,
    }));

  const inputFormulaRef = useRef<any>(null);
  const handleClickShowDropdown = (type: "function" | "field_code") => {
    if (inputFormulaRef?.current) {
      inputFormulaRef?.current?.focus();
      setTimeout(() => {
        setDisplayDropdown({ type, isOpen: true });
      }, 250);
    }
  };

  const handleOnFocusInput = () => {
    setDisplayDropdown((oldState) => ({
      ...oldState,
      isOpen: true,
    }));
  };

  const handleOnBlurInput = () => {
    setTimeout(() => {
      setDisplayDropdown((oldState) => ({ ...oldState, isOpen: false }));
    }, 200);
  };

  const handleClickOption = (val: string) => {
    if (inputFormulaRef.current) {
      const start = (inputFormulaRef?.current as any)?.input?.selectionStart;
      const end = (inputFormulaRef?.current as any)?.input?.selectionEnd;

      const valueFormula = form?.getFieldValue("formula");
      if (start && end) {
        const updatedValue = [
          valueFormula?.substring(0, start),
          val,
          valueFormula?.substring(end),
        ].join("");
        form.setFieldValue("formula", updatedValue);
      } else {
        form.setFieldValue("formula", val);
      }
      setDisplayDropdown((oldState) => ({ ...oldState, isOpen: true }));

      setTimeout(() => {
        const isSelectFunction = displayDropdown?.type === "function";
        inputFormulaRef.current.focus();
        inputFormulaRef.current.setSelectionRange(
          start + val.length - Number(isSelectFunction),
          start + val.length - Number(isSelectFunction),
        );
      }, 250);
    }
  };

  return (
    <Styles>
      <Form.Item name={["item_config_data", "minW"]} hidden />
      <Form.Item name={["item_config_data", "w"]} hidden />
      <Form.Item
        label="フィールド名"
        name="name"
        rules={[{ required: true, message: ERRORS_MESSAGE.REQUIRED }]}
        className={`${getFieldClassName?.("name")}`}
      >
        <Input />
      </Form.Item>
      <Form.Item
        valuePropName="checked"
        name="is_hide_name"
        style={{ marginBottom: "0.5rem" }}
      >
        <Checkbox>フィールド名を表示しない</Checkbox>
      </Form.Item>
      <div className="d-flex">
        <div className="ant-form-item-label">
          <label className="ant-form-item-required">公式</label>
        </div>
        <Button
          className="btn-insert ml-2"
          label="挿入関数"
          onClick={() => handleClickShowDropdown("function")}
        />
        <Button
          className="btn-insert ml-2"
          label="フィールドコードを挿入"
          onClick={() => handleClickShowDropdown("field_code")}
        />
      </div>

      <StylesInputFormula>
        <Form.Item name="formula" className="mt-2 mb-2">
          <Input
            ref={inputFormulaRef}
            onFocus={handleOnFocusInput}
            onBlur={handleOnBlurInput}
          />
        </Form.Item>
      </StylesInputFormula>
      {displayDropdown?.isOpen && (
        <div className="dropdown-formular-wrapper">
          <div className="dropdown-formula ant-dropdown-menu ant-dropdown-menu-root ant-dropdown-menu-vertical ant-dropdown-menu-light">
            {(displayDropdown?.type === "function"
              ? functionOptions
              : fieldCodeOptions
            )?.map((item) => (
              <div
                key={item?.value}
                onClick={() => handleClickOption(item?.value || "")}
                className="p-2 ant-dropdown-menu-item ant-dropdown-menu-item-only-child"
              >
                <div className="d-flex">
                  <div className="wrap-tag">
                    {displayDropdown?.type === "function"
                      ? "Function"
                      : "Field Code"}
                  </div>
                  <div className="title-tag">{item?.label}</div>
                </div>

                <div className="description-tag">
                  Field name: {item?.description || ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Form.Item
        valuePropName="checked"
        name="is_hide_formula"
        style={{ marginBottom: "0.5rem" }}
      >
        <Checkbox>式を隠す</Checkbox>
      </Form.Item>
      <Form.Item label="" name="formula_display_type" style={{ marginTop: 10 }}>
        <Radio.Group>
          <Space direction="vertical">
            {formulaDisplayType?.map((i) => (
              <Radio key={i?.value} value={i?.value}>
                {i?.label}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </Form.Item>
      <Form.Item
        name="number_of_decimal_place"
        label="表示する小数点以下の桁数"
      >
        <Input />
      </Form.Item>
      <div className="d-flex">
        <Form.Item name="unit_of_measure" label="測定単位">
          {/* <Input className="align-right" /> */}
          <Input />
        </Form.Item>
        <Form.Item
          label=""
          name="unit_of_measure_type"
          className="radio-item"
          initialValue={UNIT_OF_MEASURE_TYPE.suffix}
        >
          {" "}
          <StylesRadioItem>
            <Radio.Group
              options={[
                {
                  label: "前に付ける（例：¥100）",
                  value: UNIT_OF_MEASURE_TYPE.frefix,
                },
                {
                  label: "後ろに付ける（例：100km）",
                  value: UNIT_OF_MEASURE_TYPE.suffix,
                },
              ]}
              defaultValue={UNIT_OF_MEASURE_TYPE.suffix}
            />
          </StylesRadioItem>
        </Form.Item>
      </div>
      <Form.Item
        label="フィルードコード"
        name="field_code"
        rules={[{ required: true, message: ERRORS_MESSAGE.REQUIRED }]}
        className={`${getFieldClassName?.("field_code")}`}
      >
        <Input />
      </Form.Item>
    </Styles>
  );
};

export default CalculatedSetting;
